

























import { PropType, computed, defineComponent, ref } from '@vue/composition-api';
import { useVModel } from '@vueuse/core';
import { createNamespacedHelpers, useStore } from 'vuex-composition-helpers';

import {
  WorkflowFormSubmissionComment,
  WorkflowFormSubmissionCommentAttachment,
  IWorkflowFormSubmissionCommentAttachment,
  ISystemError,
  IWorkflowFormSubmissionCommentData
} from '@/models';

import { setErrorFromAxiosError } from '@/utils/helpers';

import QuillEditor from '@/components/common/QuillEditor.vue';
import WorkflowFormSubmissionCommentListItem from '@/components/workflow-form/WorkflowFormSubmissionCommentListItem.vue';
import WorkflowFormSubmissionCommentAttachmentList from '@/components/workflow-form/WorkflowFormSubmissionCommentAttachmentList.vue';

import { IRootState } from '@/store';
import {
  IWorkflowFormActions,
  IWorkflowFormGetters,
  IWorkflowFormMutations,
  IWorkflowFormState,
  IWorkflowFormSubmissionCommentUpdateActionPayload
} from '@/store/workflow-form';
import { AxiosError } from 'axios';

export default defineComponent({
  name: 'WorkflowFormSubmissionCommentField',

  components: {
    QuillEditor,
    WorkflowFormSubmissionCommentListItem,
    WorkflowFormSubmissionCommentAttachmentList
  },

  props: {
    value: {
      type: Object as PropType<IWorkflowFormSubmissionCommentData>,
      default: () => ({} as IWorkflowFormSubmissionCommentData)
    },

    submissionId: {
      type: Number,
      required: true
    }
  },

  emits: {
    'clear-errors': () => true
  },

  setup(props, { emit }) {
    const comment = useVModel(props, 'value');

    const store = useStore<IRootState>();

    const { useActions, useGetters } = createNamespacedHelpers<
      IWorkflowFormState,
      IWorkflowFormGetters,
      IWorkflowFormActions,
      IWorkflowFormMutations
    >(store, 'workflowForm');

    const { fetchWorkflowFormSubmissionCommentsMentionableUsers } = useActions([
      'fetchWorkflowFormSubmissionCommentsMentionableUsers'
    ]);

    const {
      getWorkflowFormSubmissionMentionableUsers: mentionableUsers
    } = useGetters(['getWorkflowFormSubmissionMentionableUsers']);

    const mentionsLoading = ref(false);

    const mentionsErrorMessage = ref<ISystemError | null>(null);

    function clearMentionErrors() {
      mentionsErrorMessage.value = null;
    }

    async function fetchMentionableUsers() {
      clearMentionErrors();

      mentionsLoading.value = true;
      try {
        await fetchWorkflowFormSubmissionCommentsMentionableUsers(
          props.submissionId
        );
      } catch (error) {
        mentionsErrorMessage.value = setErrorFromAxiosError(
          error as AxiosError
        );
      } finally {
        mentionsLoading.value = false;
      }
    }

    const commentAttachments = ref<IWorkflowFormSubmissionCommentAttachment[]>(
      []
    );

    function addBaseAttachments(files: File[]) {
      commentAttachments.value.push(
        ...files.map(
          file => new WorkflowFormSubmissionCommentAttachment({ file: file })
        )
      );
    }

    function clearAllErrors() {
      emit('clear-errors');
      clearMentionErrors();
    }

    /**
     * @public api
     */
    function addComment() {
      clearAllErrors();

      const parsedCommentMentionText = WorkflowFormSubmissionComment.parseHtmlToMentionText(
        comment.value.text
      );

      const commentData: IWorkflowFormSubmissionCommentUpdateActionPayload = {
        submissionId: props.submissionId,
        comment: { ...comment.value, text: parsedCommentMentionText }
      };

      return {
        comment: commentData,
        attachments: commentAttachments.value
      };
    }

    /**
     * @public api
     */
    function reset() {
      commentAttachments.value = [];
      comment.value = {
        text: ''
      } as IWorkflowFormSubmissionCommentData;
    }

    return {
      comment,
      mentionableUsers,
      mentionsLoading,

      fetchMentionableUsers,
      commentAttachments,
      addBaseAttachments,
      addComment,
      reset
    };
  }
});
